import { vocab } from '@/ds';
import { told } from '@/app/events/curateConfirm';
import { suggestionWords } from '@/features/rules/vocab';
import type { Schemas } from '@/lib/api/client';
import { formatNumber } from '@/lib/format/format';

// The console's words (the design system's vocab keys) for the API's staged-event values.

export const CANDIDATE_TABS = ['pending', 'published', 'linked', 'rejected', 'expired'] as const;
export type CandidateTab = (typeof CANDIDATE_TABS)[number];

const STATUS_API: Record<CandidateTab, string> = {
  pending: 'PendingReview',
  published: 'Approved',
  linked: 'Approved',
  rejected: 'Rejected',
  expired: 'Expired',
};

// Both are Approved; an approval with no recorded decision counts as Published.
const DECISIONS_API: Partial<Record<CandidateTab, string[]>> = {
  published: ['Published', 'None'],
  linked: ['Linked'],
};

export const statusToApi = (tab: CandidateTab): string => STATUS_API[tab];

export const decisionsToApi = (tab: CandidateTab): string[] | undefined => DECISIONS_API[tab];

/** The tab a record sits under: its status, with an approved one split by how it was decided. */
export function candidateStatus(item: {
  approvalStatus: string;
  decision?: string | null;
}): CandidateTab | undefined {
  if (item.approvalStatus === 'Approved') {
    return item.decision === 'Linked' ? 'linked' : 'published';
  }
  return CANDIDATE_TABS.find((tab) => STATUS_API[tab] === item.approvalStatus);
}

type StatsEntry = Schemas['StagedEventStatsEntryDto'];

/** The tabs' counts from the stats: the approved split into Linked and the rest. */
export function tabCounts(stats: StatsEntry[]): Map<CandidateTab, number> {
  const counts = new Map<CandidateTab, number>();
  const add = (tab: CandidateTab, n: number) => counts.set(tab, (counts.get(tab) ?? 0) + n);
  for (const entry of stats) {
    if (entry.approvalStatus === 'Approved') {
      add('linked', entry.linkedCount);
      add('published', entry.count - entry.linkedCount);
      continue;
    }
    const tab = candidateStatus(entry);
    if (tab) add(tab, entry.count);
  }
  return counts;
}

/** Warnings the list can filter by, each one API warning kind. Possibly cancelled has its own filter. */
export const WARNING_FILTERS = [
  'missing-city',
  'low-confidence',
  'no-start-time',
  'date-inferred',
  'no-races',
  'incomplete-race',
  'outside-region',
  'source-changed',
  'cancelled-at-source',
  'postponed-at-source',
] as const;
export type WarningFilter = (typeof WARNING_FILTERS)[number];

type ApiWarning = WarningFilter | 'possibly-cancelled';

const WARNING_API: Record<ApiWarning, string> = {
  'missing-city': 'MissingCity',
  'low-confidence': 'LowConfidence',
  'no-start-time': 'NoStartTime',
  'date-inferred': 'DateInferred',
  'no-races': 'NoRaces',
  'incomplete-race': 'IncompleteRace',
  'outside-region': 'OutOfRegion',
  'source-changed': 'SourceChangedAfterEdit',
  'possibly-cancelled': 'PossiblyCancelled',
  'cancelled-at-source': 'CancelledAtSource',
  'postponed-at-source': 'PostponedAtSource',
};

export const warningToApi = (warning: WarningFilter): string => WARNING_API[warning];

export type CandidateWarning = ApiWarning | 'duplicate-suspected' | 'changed-published';

/** Mirrors the API's LowConfidenceThreshold: below it, a record carries "Low confidence". */
export const TRUST_BAR = 0.7;

// Blocking first, then what most often needs a look.
const ORDER: CandidateWarning[] = [
  'missing-city',
  'changed-published',
  'duplicate-suspected',
  'possibly-cancelled',
  'cancelled-at-source',
  'postponed-at-source',
  'source-changed',
  'low-confidence',
  'no-races',
  'incomplete-race',
  'outside-region',
  'date-inferred',
  'no-start-time',
];

/** A review queue's title: its tab, then each filter that narrows it. */
export function queueTitle(search: {
  status: string;
  changed?: boolean;
  warning?: WarningFilter;
}): string {
  const narrowing = [
    search.changed ? vocab.WARNINGS['changed-published']?.short : undefined,
    search.warning ? vocab.WARNINGS[search.warning]?.label : undefined,
  ].flatMap((words) => (words ? [words.toLowerCase()] : []));
  const tab = vocab.STATUS.candidate?.[search.status]?.label ?? search.status;
  return [tab, ...narrowing].join(', ');
}

const sortWarnings = (warnings: CandidateWarning[]) =>
  [...new Set(warnings)].sort((a, b) => ORDER.indexOf(a) - ORDER.indexOf(b));

type ListItem = Schemas['StagedEventListItemDto'];

/** Every warning on a record: the API's own list, plus the console's two states. */
export function recordWarnings(summary: ListItem): CandidateWarning[] {
  const warnings: CandidateWarning[] = [];
  for (const kind of summary.warnings ?? []) {
    const key = (Object.keys(WARNING_API) as ApiWarning[]).find((w) => WARNING_API[w] === kind);
    if (key) warnings.push(key);
  }
  if (summary.inOpenCluster) warnings.push('duplicate-suspected');
  if (isChangedPublished(summary)) warnings.push('changed-published');
  return sortWarnings(warnings);
}

/** The summary of a source change stays after the decision, so only a pending one waits. */
export const isChangedPublished = (
  item: Pick<ListItem, 'approvalStatus' | 'diffSummary'>,
): boolean => item.approvalStatus === 'PendingReview' && !!item.diffSummary;

/** A pending linked record joins its event when published, unless it's a published event back with changes. */
export const joinsEvent = (item: ListItem): boolean =>
  item.approvalStatus === 'PendingReview' && !!item.linkedRaceEventId && !isChangedPublished(item);

/** The API's names for the fields a record's `fieldEdits` can list. */
export type EditedField =
  | 'Name'
  | 'Date'
  | 'StartTime'
  | 'City'
  | 'Country'
  | 'Description'
  | 'Coordinates'
  | 'Image'
  | 'Races';

/** Who set a value when the edit's entry names nobody. */
export const UNNAMED_EDITOR = 'an operator';

export type LinkOrigin = 'pipeline' | 'person' | 'rule' | 'unrecorded';

/** Who made a record's link, in the design system's words; a person or rule with no name isn't recorded. */
export function linkOrigin(linkedBy: ListItem['linkedBy']): {
  origin: LinkOrigin;
  name: string | null;
} {
  const name = linkedBy?.name || null;
  if (linkedBy?.kind === 'Pipeline') return { origin: 'pipeline', name: null };
  if (linkedBy?.kind === 'Person' && name) return { origin: 'person', name };
  if (linkedBy?.kind === 'Rule' && name) return { origin: 'rule', name };
  return { origin: 'unrecorded', name: null };
}

/** The rejection "Keep current version" records; the published event stays as it is. */
export const KEEP_REASON = 'Kept the published version';

/** What a rejection sends: the reason text the trail keeps, and the API's kind for it. */
export interface Rejection {
  reason: string;
  kind: string;
}

export const KEEP_REJECTION: Rejection = { reason: KEEP_REASON, kind: 'WrongData' };

/** Whether publishing is blocked, by a warning the design system marks blocking. */
export const isBlocking = (warning: CandidateWarning): boolean =>
  !!vocab.WARNINGS[warning]?.blocking;

/** The reasons the reject dialog offers. "Duplicate" is set only by resolving a duplicate group. */
export const REJECT_REASONS = [
  'Not a race',
  'Outside the region',
  'Date in the past',
  'Cancelled by organizer',
  'Wrong or unusable data',
  'Other',
] as const;
export type RejectReason = (typeof REJECT_REASONS)[number];

// Typed by the reasons, so a new one can't be added without its kind.
const REJECT_KIND: Record<RejectReason, string> = {
  'Not a race': 'NotARaceEvent',
  'Outside the region': 'OutsideRegion',
  'Date in the past': 'PastOrCancelled',
  'Cancelled by organizer': 'PastOrCancelled',
  'Wrong or unusable data': 'WrongData',
  Other: 'Other',
};

/** A reason with its note, as the trail keeps it, and the kind that reason stands for. */
export const rejectionFor = (reason: RejectReason, note: string): Rejection => ({
  reason: note.trim() ? `${reason}: ${note.trim()}` : reason,
  kind: REJECT_KIND[reason],
});

/** The link to the rule that suggested a record: its action, then its name. */
export const ruleLinkName = (suggestion: Parameters<typeof suggestionWords>[0]): string =>
  `${suggestionWords(suggestion)}: ${suggestion.ruleName}`;

/** Said once Still on lands: the hold ends only when the page is seen and then missed again. */
export const stillOnDone = (name: string): string =>
  `Marked “${name}” still on. It won't be flagged again until its page is seen and then missed again.`;

/** Said when the reads that decide a published record's actions fail. */
export const PUBLISHED_UNREAD_TITLE = "Couldn't load what this event allows";

/** What removing an event takes with it, and that nobody is told; Remove from the app can't be undone. */
export function removeText(interest: number | undefined): string {
  if (interest === 0) {
    return "The event and its races are removed from the app. Nobody has marked interest in it, and nobody is told. This can't be undone.";
  }
  const runners =
    interest === undefined
      ? "the runners' interest in it"
      : `the ${formatNumber(interest)} runner${interest === 1 ? "'s" : "s'"} interest in it`;
  return `The event, its races and ${runners} are removed from the app. Nobody is told. This can't be undone.`;
}

type Facts = Pick<Schemas['EventFactsDto'], 'isCuratorOwned' | 'isCancelled' | 'date'>;

/** What a person can do about the event a record published. */
export interface PublishedOffer {
  canCancel: boolean;
  canMarkStillOn: boolean;
  canRemove: boolean;
  canUndo: boolean;
}

/**
 * The choices for a record linked to a live event: Mark cancelled for a curator's upcoming event
 * that isn't cancelled, Still on while flagged, and Remove from the app only when the publish
 * can't be undone, which offers Undo instead. A record that joined an event never removes it.
 */
export function publishedOffer({
  summary,
  facts,
  undoAvailable,
  today,
}: {
  summary: ListItem;
  facts: Facts | undefined;
  undoAvailable: boolean;
  today: string;
}): PublishedOffer {
  const linked = !!summary.linkedRaceEventId;
  const decided =
    linked &&
    (summary.approvalStatus === 'Approved' ||
      (summary.approvalStatus === 'Rejected' && !!summary.decision));
  const curator = !!facts?.isCuratorOwned;
  return {
    canCancel: decided && curator && !facts?.isCancelled && (facts?.date ?? '') >= today,
    canMarkStillOn: decided && summary.possiblyCancelled,
    canRemove:
      summary.decision === 'Published' &&
      (decided || isChangedPublished(summary)) &&
      curator &&
      !undoAvailable,
    canUndo: (decided || isChangedPublished(summary)) && undoAvailable,
  };
}

type Changes = Schemas['StagedEventChangesDto'];

const POSTPONED_WITHOUT_DATE = 'PostponedWithoutDate';

/** How approving ends for the event and who hears of it, in the sentence the confirm carries. */
export interface ApprovalOutcome {
  text: string;
  tone: 'critical' | 'primary';
}

/**
 * What approving a record does that a person should read first, by the rule the Races module
 * follows when it revises an event: cancelled or back on wins over a new date, and a date move on a
 * cancelled event tells nobody. Null when approving says nothing worth a confirm.
 */
export function approvalOutcome(
  summary: Pick<ListItem, 'status' | 'changes'>,
): ApprovalOutcome | null {
  const c: Changes | null | undefined = summary.changes;
  if (!c) {
    return summary.status === 'cancelled'
      ? {
          text: 'It publishes as cancelled: the event goes into the app with a Cancelled badge.',
          tone: 'primary',
        }
      : null;
  }
  if (!c.eventExists || !c.changes) return null;
  const { before, after, fields } = c.changes;
  const [wasCancelled, nowCancelled] = [!!before.cancelledAt, !!after.cancelledAt];
  const message = runnersTold(
    { wasCancelled, nowCancelled, dateMoves: fields.includes('Date') },
    c.interestedRunners,
  );
  if (message) return message;
  if (c.statusKind === POSTPONED_WITHOUT_DATE) {
    return {
      text: 'The source says it is postponed with no new date, so applying leaves the date as it is. Mark it cancelled or keep the current version instead.',
      tone: 'primary',
    };
  }
  return null;
}

const capitalise = (text: string) => `${text.charAt(0).toUpperCase()}${text.slice(1)}`;

/** The one message an approval sends: cancelled or back on wins over a new date; a date move on a cancelled event tells nobody. */
function runnersTold(
  move: { wasCancelled: boolean; nowCancelled: boolean; dateMoves: boolean },
  interested: number,
): ApprovalOutcome | null {
  if (move.wasCancelled !== move.nowCancelled) {
    return move.nowCancelled
      ? {
          text: `The event will be marked cancelled, and ${told(interested, 'it is cancelled', true)}.`,
          tone: 'critical',
        }
      : {
          text: `The event will be back on, and ${told(interested, 'it is back on', true)}.`,
          tone: 'primary',
        };
  }
  if (move.dateMoves && !move.wasCancelled) {
    return { text: `${capitalise(told(interested, 'the new date', true))}.`, tone: 'primary' };
  }
  return null;
}

/**
 * The words the approval confirm uses: what is decided, and the sentence's lead before the record's
 * name. A changed event applies; a linked record, or one linked to a chosen event, links to it.
 */
export function approvalConfirmWords(
  summary: Pick<ListItem, 'approvalStatus' | 'diffSummary' | 'linkedRaceEventId'>,
  target?: string | null,
): { decision: string; lead: string; target: string | undefined } {
  if (isChangedPublished(summary)) {
    return { decision: vocab.DECISIONS.apply, lead: 'Apply the changes to', target: undefined };
  }
  if (target || summary.linkedRaceEventId) {
    return {
      decision: vocab.DECISIONS.link,
      lead: vocab.DECISIONS.link,
      target: target ?? undefined,
    };
  }
  return { decision: vocab.DECISIONS.publish, lead: vocab.DECISIONS.publish, target: undefined };
}

type LinkFacts = Facts & Pick<Schemas['EventFactsDto'], 'interestCount'>;

/**
 * What linking a record to the event it chose does to runners, from the record and the event's
 * facts: only a curator's event is written, and a status moves it only as approval reads it.
 */
export function linkOutcome(
  summary: Pick<ListItem, 'status' | 'date' | 'diffSummary'>,
  facts: LinkFacts,
): ApprovalOutcome | null {
  if (!facts.isCuratorOwned) return null;
  const statusMoved = (summary.diffSummary ?? '').split(',').some((f) => f.trim() === 'status');
  const nowCancelled =
    summary.status === 'cancelled' ||
    (facts.isCancelled && !(summary.status === 'scheduled' && statusMoved));
  return runnersTold(
    { wasCancelled: facts.isCancelled, nowCancelled, dateMoves: summary.date !== facts.date },
    facts.interestCount,
  );
}
